import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { eventsApi } from '~/entities/event/api'
import { listingsApi } from '~/entities/listing/api'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { MINUTES } from '~/shared/lib/time-constants'

interface PreloadConfig {
  enabled?: boolean
  delay?: number
  waitForIdle?: boolean
}

const PRELOAD_PAGE_SIZE = 20

/** Pre-warm listings + upcoming/past events for a business after dashboard mount. */
export function useSmartPreload(businessId: string | undefined, config: PreloadConfig = {}) {
  const queryClient = useQueryClient()
  const hasPreloaded = useRef(false)

  const { enabled = true, delay = 1000, waitForIdle = true } = config

  useEffect(() => {
    if (!enabled || hasPreloaded.current || !businessId) {
      return
    }

    hasPreloaded.current = true

    const preloadData = async () => {
      try {
        await Promise.all([
          queryClient.prefetchInfiniteQuery({
            queryKey: queryKey(QueryKey.LISTINGS, businessId),
            queryFn: () =>
              listingsApi.list(businessId, {
                startIndex: 0,
                pageSize: PRELOAD_PAGE_SIZE,
              }),
            initialPageParam: 0,
            staleTime: 2 * MINUTES,
          }),
          queryClient.prefetchInfiniteQuery({
            queryKey: queryKey(QueryKey.EVENTS, businessId, 'upcoming'),
            queryFn: () =>
              eventsApi.list(businessId, {
                startIndex: 0,
                pageSize: PRELOAD_PAGE_SIZE,
                when: 'upcoming',
              }),
            initialPageParam: 0,
            staleTime: 10 * MINUTES,
          }),
          queryClient.prefetchInfiniteQuery({
            queryKey: queryKey(QueryKey.EVENTS, businessId, 'past'),
            queryFn: () =>
              eventsApi.list(businessId, {
                startIndex: 0,
                pageSize: PRELOAD_PAGE_SIZE,
                when: 'past',
              }),
            initialPageParam: 0,
            staleTime: 10 * MINUTES,
          }),
        ])
      } catch (error) {
        log.error(error as Error, '[useSmartPreload] Failed to preload data')
      }
    }

    const schedulePreload = () => {
      setTimeout(() => {
        if (waitForIdle && 'requestIdleCallback' in window) {
          requestIdleCallback(
            () => {
              preloadData()
            },
            { timeout: 5000 },
          )
        } else {
          preloadData()
        }
      }, delay)
    }

    schedulePreload()
  }, [businessId, enabled, delay, waitForIdle, queryClient])
}
